/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { isNil } from 'lodash'

import {
  EuiButton,
  EuiButtonGroup,
  EuiCheckbox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  EuiSpacer,
  EuiSuperSelect,
  EuiText,
  EuiTextArea,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useProfile } from '@modules/profile-lib/hooks'

// eslint-disable-next-line import/no-restricted-paths
import { isElasticStaff } from '@/lib/validateEmail'

import useEbtDeletionSurvey from './useEbtDeletionSurvey'
import { DELETION_REASONS, SCORE_OPTIONS } from './options'

import type { DeletionReasonsValues } from './options'
import type { FC } from 'react'

const MAX_REASON_FOR_SCORE_LENGTH = 5000

export interface DeletionSurveyData {
  score: number
  reasonForScore: string
  deletionReason: DeletionReasonsValues
  openToFollowUp: boolean
}

export interface ResourceContext {
  type: 'serverless' | 'hosted'
  solution?: string
  region?: string
}

export interface DeletionSurveyModalProps {
  resourceType: string
  resourceTypeShort: string
  resourceTypePlural: string
  architectureType: string
  resourceContext: ResourceContext
  onClose: () => void
  isLoading?: boolean
}

const DeletionSurveyModal: FC<DeletionSurveyModalProps> = ({
  resourceType,
  resourceTypeShort,
  architectureType,
  resourceTypePlural,
  resourceContext,
  onClose,
  isLoading,
}) => {
  const { formatMessage } = useIntl()

  const modalTitleId = useGeneratedHtmlId({ prefix: 'deletion-survey-modal-title' })

  const { reportDeletionSurveyCloseEbt, reportDeletionSurveySubmitEbt } = useEbtDeletionSurvey()

  const [scoreId, setScoreId] = useState<string | undefined>()
  const [reasonForScore, setReasonForScore] = useState('')
  const [deletionReason, setDeletionReason] = useState<DeletionReasonsValues | undefined>()
  const [openToFollowUp, setOpenToFollowUp] = useState(true)

  const profile = useProfile()
  const isElasticStaffUser = profile?.email ? isElasticStaff(profile.email) : false

  const onDeletionReasonChange = (value: DeletionReasonsValues) => {
    setDeletionReason(value)
  }

  const handleCloseButtonClick = () => {
    if (!isElasticStaffUser) {
      reportDeletionSurveyCloseEbt(resourceContext)
    }

    onClose()
  }

  const currentScoreValue = SCORE_OPTIONS.find((option) => option.id === scoreId)?.label

  const isScoreEmpty = isNil(currentScoreValue)
  const isReasonForScoreEmpty = reasonForScore.length === 0
  const isDeletionReasonEmpty = !deletionReason
  const isSubmitDisabled = isScoreEmpty || isReasonForScoreEmpty || isDeletionReasonEmpty

  const handleSubmit = () => {
    if (isSubmitDisabled) {
      return
    }

    const formData = {
      score: currentScoreValue,
      reasonForScore,
      deletionReason,
      openToFollowUp,
    }

    if (!isElasticStaffUser) {
      reportDeletionSurveySubmitEbt({
        ...resourceContext,
        ...formData,
      })
    }

    onClose()
  }

  return (
    <EuiOverlayMask>
      <EuiModal
        onClose={handleCloseButtonClick}
        css={{ minWidth: 510 }}
        data-test-id='deletion-survey-modal'
        aria-labelledby={modalTitleId}
      >
        <EuiModalHeader>
          <EuiModalHeaderTitle id={modalTitleId}>
            <FormattedMessage
              id='deletion-survey-modal.title'
              defaultMessage='Help us improve {resourceTypePlural}'
              values={{ resourceTypePlural }}
            />
          </EuiModalHeaderTitle>
        </EuiModalHeader>

        <EuiModalBody>
          <EuiForm component='div'>
            <EuiFormRow
              label={
                <FormattedMessage
                  id='deletion-survey-form.score.label'
                  defaultMessage='How likely are you to recommend a {resourceType} to a friend?'
                  values={{ resourceType }}
                />
              }
              fullWidth={true}
            >
              <EuiFlexGroup direction='column' gutterSize='xs'>
                <EuiSpacer size='xs' />
                <EuiFlexItem>
                  <EuiFlexGroup justifyContent='spaceBetween' gutterSize='none'>
                    <EuiFlexItem grow={false}>
                      <EuiText size='xs' color='subdued'>
                        <FormattedMessage
                          id='deletion-survey-form.score.not-at-all-likely'
                          defaultMessage='Not at all likely'
                        />
                      </EuiText>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiText size='xs' color='subdued'>
                        <FormattedMessage
                          id='deletion-survey-form.score.extremely-likely'
                          defaultMessage='Extremely likely'
                        />
                      </EuiText>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiButtonGroup
                    color='primary'
                    isFullWidth={true}
                    buttonSize='m'
                    type='single'
                    options={SCORE_OPTIONS}
                    idSelected={scoreId ?? ''}
                    onChange={(id) => setScoreId(id)}
                    legend={formatMessage(
                      {
                        id: 'deletion-survey-form.score.legend',
                        defaultMessage: 'Score for {resourceType}',
                      },
                      { resourceType },
                    )}
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFormRow>

            <EuiSpacer size='l' />

            <EuiFormRow
              label={
                <FormattedMessage
                  id='deletion-survey-form.reason-for-score.label'
                  defaultMessage='Reason for your score'
                />
              }
              fullWidth={true}
            >
              <EuiTextArea
                maxLength={MAX_REASON_FOR_SCORE_LENGTH}
                fullWidth={true}
                rows={3}
                resize='vertical'
                placeholder={formatMessage(
                  {
                    id: 'deletion-survey-form.reason-for-score.placeholder',
                    defaultMessage: 'My experience with {architectureType} was...',
                  },
                  { architectureType },
                )}
                value={reasonForScore}
                onChange={(e) => setReasonForScore(e.target.value)}
              />
            </EuiFormRow>

            <EuiSpacer size='l' />

            <EuiFormRow
              label={
                <FormattedMessage
                  id='deletion-survey-form.reason-for-deletion.label'
                  defaultMessage="What's the primary reason for deleting this {resourceTypeShort}?"
                  values={{ resourceTypeShort }}
                />
              }
              fullWidth={true}
            >
              <EuiSuperSelect
                fullWidth={true}
                placeholder={formatMessage({
                  id: 'deletion-survey-modal.reason-for-deletion.placeholder',
                  defaultMessage: 'Make a selection',
                })}
                options={DELETION_REASONS}
                valueOfSelected={deletionReason}
                onChange={onDeletionReasonChange}
                data-test-id='deletion-survey-reason-select'
              />
            </EuiFormRow>

            <EuiSpacer size='l' />

            <EuiCheckbox
              id='open-to-follow-up-checkbox'
              label={
                <FormattedMessage
                  id='deletion-survey-form.open-to-follow-up-checkbox.label'
                  defaultMessage="I'm open to follow-up outreach from the product team"
                />
              }
              checked={openToFollowUp}
              onChange={(e) => setOpenToFollowUp(e.target.checked)}
            />
          </EuiForm>
        </EuiModalBody>

        <EuiModalFooter>
          <EuiButton
            isLoading={isLoading}
            isDisabled={isSubmitDisabled}
            onClick={handleSubmit}
            fill={true}
            data-test-id='deletion-survey-submit'
          >
            <FormattedMessage id='deletion-survey-modal.submit' defaultMessage='Submit' />
          </EuiButton>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  )
}

export default DeletionSurveyModal
